import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { CustomerService } from '../../../pages/customer-management/customer/customer.service';
import { AuthService } from '../../../auth/auth.service';
import { SnackService } from '../snack/snack.service';

@Component({
    selector: 'app-branch-business-setup-dialog',
    templateUrl: './branch-business-setup-dialog.component.html',
    styleUrls: ['./branch-business-setup-dialog.component.scss']
})
export class BranchBusinessSetupDialogComponent implements OnInit {
    CONFIG = JSON.parse(window.localStorage.getItem('CONFIG'));
    serviceType: any[] = [];
    services: any[] = [];
    servicesCategories: any[] = [];
    filteredServices: any[] = [];
    filteredServicesCategories: any[] = [];
    selectedTypeIds: number[] = [];
    selectedServiceIds: number[] = [];
    selectedCategoryIds: number[] = [];
    selectedCategories: any[] = [];
    isSaving = false;

    constructor(
        private dialogRef: MatDialogRef<BranchBusinessSetupDialogComponent>,
        public translate: TranslateService,
        private customerService: CustomerService,
        private authService: AuthService,
        private snack: SnackService
    ) { }

    ngOnInit(): void {
        this.loadConfig();
    }

    private loadConfig(): void {
        if (this.CONFIG) {
            this.serviceType = this.CONFIG.serviceType || [];
            this.services = this.CONFIG.services || [];
            this.servicesCategories = this.CONFIG.servicesCategories || [];
        }
    }

    selectedType(typeId: number): boolean {
        return this.selectedTypeIds.includes(typeId);
    }

    selectSpecificType(typeId: number): void {
        if (this.selectedType(typeId)) {
            this.selectedTypeIds = [];
        } else {
            this.selectedTypeIds = [typeId];
        }
        this.refreshFilteredServices();
    }

    selectedService(serviceId: number): boolean {
        return this.selectedServiceIds.includes(serviceId);
    }

    selectSpecificService(serviceId: number): void {
        if (this.selectedService(serviceId)) {
            this.selectedServiceIds = [];
        } else {
            this.selectedServiceIds = [serviceId];
        }
        this.refreshFilteredServicesCategories();
    }

    refreshFilteredServices(): void {
        this.filteredServices = this.services.filter((s) => this.selectedTypeIds.includes(s.serviceType?.id));
        const availableServiceIds = this.filteredServices.map((s) => s.id);
        this.selectedServiceIds = this.selectedServiceIds.filter((id) => availableServiceIds.includes(id));
        this.refreshFilteredServicesCategories();
    }

    refreshFilteredServicesCategories(): void {
        this.filteredServicesCategories = this.servicesCategories.filter((s) => this.selectedServiceIds.includes(s.service?.id));
        this.selectedCategories = this.selectedCategories.filter(cat => this.selectedServiceIds.includes(cat?.service?.id));
        this.selectedCategoryIds = this.selectedCategories.map(c => c.id);
    }

    isCategorySelected(category: any): boolean {
        return this.selectedCategoryIds.includes(category.id);
    }

    onCategoryChange(category: any, event: any): void {
        const isChecked = event.checked !== undefined ? event.checked : event.target?.checked;
        if (isChecked) {
            this.selectedCategories.push(category);
            this.selectedCategoryIds.push(category.id);
        } else {
            this.selectedCategories = this.selectedCategories.filter(c => c.id !== category.id);
            this.selectedCategoryIds = this.selectedCategoryIds.filter(id => id !== category.id);
        }
    }

    selectAllCategories(): void {
        this.filteredServicesCategories.forEach(category => {
            if (!this.isCategorySelected(category)) {
                this.selectedCategories.push(category);
                this.selectedCategoryIds.push(category.id);
            }
        });
    }

    clearAllCategories(): void {
        const categoryIdsToRemove = this.filteredServicesCategories.map(cat => cat.id);
        this.selectedCategories = this.selectedCategories.filter(c => !categoryIdsToRemove.includes(c.id));
        this.selectedCategoryIds = this.selectedCategoryIds.filter(id => !categoryIdsToRemove.includes(id));
    }

    get isValid(): boolean {
        return this.selectedTypeIds.length > 0 &&
            this.selectedServiceIds.length > 0 &&
            (this.filteredServicesCategories.length === 0 || this.selectedCategoryIds.length > 0);
    }

    save(): void {
        if (!this.isValid || this.isSaving) return;

        this.isSaving = true;
        const currentUser = JSON.parse(localStorage.getItem('CURRENT_USER'));
        const branches = JSON.parse(localStorage.getItem('CUSTOMER_BRANCHES')) || [];
        const branchId = branches.length > 0 ? branches[0].id : null;

        const selectedServiceCategories = this.selectedCategories
            .filter(cat => this.selectedServiceIds.includes(cat?.service?.id));

        const requestObj: any = {
            services: this.selectedServiceIds.map((id) => ({ id })),
            serviceCategories: selectedServiceCategories,
            customer: { id: currentUser.customerId }
        };

        if (branchId) {
            requestObj.id = branchId;
            this.customerService.updateBranchBusiness(requestObj).subscribe({
                next: (res) => {
                    if (res.success) {
                        this.updateCachedBranchData(branchId, requestObj);
                        this.refreshBranchesCache();
                        this.snack.showSnack('COMMON.EDIT_SUCCESS', 'success');
                        this.dialogRef.close(true);
                    }
                    this.isSaving = false;
                },
                error: () => {
                    this.isSaving = false;
                }
            });
        } else {
            this.isSaving = false;
            this.dialogRef.close(true);
        }
    }

    private updateCachedBranchData(branchId: number, requestObj: any): void {
        const branches = JSON.parse(localStorage.getItem('CUSTOMER_BRANCHES')) || [];
        const branchIndex = branches.findIndex((b: any) => b.id === branchId);
        if (branchIndex !== -1) {
            branches[branchIndex].services = requestObj.services;
            branches[branchIndex].serviceCategories = requestObj.serviceCategories;
            localStorage.setItem('CUSTOMER_BRANCHES', JSON.stringify(branches));
        }
    }

    private refreshBranchesCache(): void {
        this.authService.getCustomerBranches().subscribe((res) => {
            if (res?.data) {
                localStorage.setItem('CUSTOMER_BRANCHES', JSON.stringify(res.data));
            }
        });
    }
}
